import { put, select } from 'redux-saga/effects';
import notification from 'antd/lib/notification';
// internal
import history from '@app/components/router/history';
import * as ActionType from '@app/store/typings/actions/project.d';
import * as projectService from '@app/services/project';
import { handleError, convertErrorToResponse } from '@app/utils/service';
import { RootState } from '@app/store/typings/state/root';
import {
  getBrand,
  getProject,
  setProject,
  setProjectName,
  setLockerZone,
  setProjectNoOfBookRoomSameTime,
} from '@app/utils/localStorage';
import { ResponseMeta } from '@app/typings/services/service';

export function* fetchData({ payload }: ActionType.FetchData) {
  try {
    const { data } = yield projectService.fetchProject(
      payload.brand,
      payload?.name,
      payload?.offset,
      payload?.limit,
    );
    if (data.success) {
      yield put<ActionType.ProjectType>({
        type: 'KEY_PROJECT_SET_DATA',
        payload: {
          meta: data.meta,
          data: data.data,
        },
      });
    }
  } catch (error: any) {
    handleError(error);
  }
}

export function* fetchWithId({ payload }: ActionType.FetchProjectWithId) {
  try {
    yield put<ActionType.ProjectType>({
      type: 'KEY_PROJECT_CLEAR_CURRENTID',
    });
    const id = payload ? payload : getProject();
    const { data } = yield projectService.fetchProjectWithId(id);

    if (data.success) {
      setProjectNoOfBookRoomSameTime(data.data.config?.noOfBookRoomSameTime || 'false');
      yield put<ActionType.ProjectType>({
        type: 'KEY_PROJECT_SET_WITHID',
        payload: data.data,
      });
    }
  } catch (error: any) {
    handleError(error);
  }
}

export function* selectId({ payload }: ActionType.SelectID) {
  setProject(payload.id);
  setProjectName(payload.name);
  setLockerZone('');
  yield put<ActionType.FetchProjectWithId>({ type: 'KEY_PROJECT_FETCH_WITHID', payload: '' });
}

export function* createProject({ payload }: ActionType.CreateProject) {
  try {
    const { data } = yield projectService.createProject(payload.brandId, payload.data);
    if (data.success) {
      if (payload.data.file) {
        yield put<ActionType.ProjectType>({
          type: 'KEY_PROJECT_UPLOAD_PICTURE',
          payload: {
            projectId: data.data._id,
            file: payload.data.file,
          },
        });
      }
      notification.success({ message: 'Success', description: 'Create project successfully' });
      history.push('/project');
    }
  } catch (error: any) {
    const message = error.response.data.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}

export function* updateProject({ payload }: ActionType.UpdateProject) {
  try {
    const projectId: string = yield select((state: RootState) => state.project.currentProject._id);
    const { data } = yield projectService.updateProject(
      projectId,
      payload.data,
      payload.isSameName,
    );
    if (data.success) {
      if (payload.data.file) {
        yield put<ActionType.ProjectType>({
          type: 'KEY_PROJECT_UPLOAD_PICTURE',
          payload: {
            projectId: data.data._id,
            file: payload.data.file,
          },
        });
      }
      if (payload.isSetting) {
        notification.success({ message: 'Success', description: 'Update project successfully' });
        const subpath = history.location.pathname.split('/');
        const pathname = subpath.slice(0, subpath.length - 1).join('/');
        history.push(pathname);
      } else {
        history.push('/project');
      }
    }
  } catch (error: any) {
    const message = error.response.data.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}

export function* deleteProject({ payload }: ActionType.DeleteProject) {
  try {
    const limit: number = yield select((state: RootState) => state.project.meta.limit);
    const total: number = yield select((state: RootState) => state.project.meta.total);
    const offset: number = yield select((state: RootState) => state.project.meta.offset);
    const realOffset =
      offset === total - payload.projects.length && offset !== 0 ? offset - limit : offset;
    let count = 0;
    for (let i = 0; i < payload.projects.length; i++) {
      const { status } = yield projectService.deleteProject(payload.projects[i]);
      if (status === 204) {
        count += 1;
      }
    }
    if (count === payload.projects.length) {
      notification.success({ message: 'Success', description: 'Delete project successfully' });
      yield put<ActionType.ProjectType>({
        type: 'KEY_PROJECT_FETCH',
        payload: {
          brand: payload.brand,
          offset: realOffset,
          limit: limit,
        },
      });
    }
  } catch (error: any) {
    const message = error.response.data.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}

export function* uploadProjectPicture({ payload }: ActionType.ProjectUploadPicture) {
  try {
    const meta: ResponseMeta = yield select((state: RootState) => state.project.meta);
    const { data } = yield projectService.uploadProjectPicture(payload.projectId, payload.file);
    if (data.success) {
      yield put<ActionType.ProjectType>({
        type: 'KEY_PROJECT_FETCH',
        payload: {
          brand: getBrand(),
          offset: meta.offset,
          limit: meta.limit,
        },
      });
    }
  } catch (error) {
    const message = convertErrorToResponse(error)?.message;
    notification.error({ message: 'Error', description: message });
    handleError(error);
  }
}
